import { useState } from 'react';
import { Link } from 'react-router-dom';

const CONTACT_EMAIL = 'subrammiles25@proton.me';

const TOPICS = [
  'General Question',
  'Bug Report',
  'Feature Request',
  'Content Correction',
  'Privacy / Data Request',
  'Other',
];

export default function ContactPage() {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState(TOPICS[0]);
  const [message, setMessage] = useState('');

  function handleSend(e) {
    e.preventDefault();
    const subject = encodeURIComponent(`[SublingoX] ${topic}${name ? ` — ${name}` : ''}`);
    const body = encodeURIComponent(message);
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12">
      <div className="mx-auto max-w-2xl">

        {/* Header */}
        <div className="mb-10">
          <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <h1 className="text-4xl font-black text-slate-900">Contact Us</h1>
          <p className="mt-3 text-slate-600">
            Have a question, found a bug, or want to suggest a language? We'd love to hear from you.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-5">

          {/* Contact form */}
          <form
            onSubmit={handleSend}
            className="sm:col-span-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-5"
          >
            <div>
              <label htmlFor="contact-name" className="block mb-1.5 text-xs font-black uppercase tracking-widest text-slate-500">
                Your name (optional)
              </label>
              <input
                id="contact-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Priya"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              />
            </div>

            <div>
              <label htmlFor="contact-topic" className="block mb-1.5 text-xs font-black uppercase tracking-widest text-slate-500">
                Topic
              </label>
              <select
                id="contact-topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
              >
                {TOPICS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="contact-message" className="block mb-1.5 text-xs font-black uppercase tracking-widest text-slate-500">
                Message
              </label>
              <textarea
                id="contact-message"
                required
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe your question or feedback..."
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 resize-none"
              />
            </div>

            <button
              type="submit"
              className="w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 py-3 text-sm font-bold text-white shadow-md shadow-indigo-600/20 transition hover:-translate-y-px hover:shadow-indigo-600/40"
            >
              Open in Email App →
            </button>

            <p className="text-center text-xs text-slate-400">
              Clicking the button will open your default email client with the message pre-filled.
            </p>
          </form>

          {/* Direct contact info */}
          <div className="sm:col-span-2 space-y-4">
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-5">
              <p className="text-xs font-black uppercase tracking-widest text-indigo-500 mb-2">Email us directly</p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-sm font-bold text-indigo-700 hover:underline break-all"
              >
                {CONTACT_EMAIL}
              </a>
              <p className="mt-2 text-xs text-indigo-600">We usually respond within 1–2 business days.</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
              <p className="text-xs font-black uppercase tracking-widest text-slate-400">Common topics</p>
              {[
                { icon: '🐛', label: 'Bug Report', desc: 'Something not working right?' },
                { icon: '💡', label: 'Feature Request', desc: 'Have an idea to improve the app?' },
                { icon: '📝', label: 'Content Correction', desc: 'Spotted a translation error?' },
                { icon: '🔒', label: 'Privacy Request', desc: 'Data access or deletion request?' },
              ].map((item) => (
                <div key={item.label} className="flex gap-2.5">
                  <span className="text-lg leading-none mt-0.5">{item.icon}</span>
                  <div>
                    <p className="text-xs font-bold text-slate-700">{item.label}</p>
                    <p className="text-xs text-slate-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer nav */}
        <div className="mt-10 flex flex-col items-center gap-2 text-center text-xs text-slate-400">
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-indigo-600 hover:underline">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="hover:text-indigo-600 hover:underline">Privacy Policy</Link>
            <Link to="/credits" className="hover:text-indigo-600 hover:underline">Credits</Link>
            <Link to="/" className="hover:text-indigo-600 hover:underline">Home</Link>
          </div>
          <p>&copy; {new Date().getFullYear()} SublingoX · Subram Miles</p>
        </div>
      </div>
    </div>
  );
}
